// web/src/api/config.ts — TTS config endpoint wrappers.
//
// Mirrors internal/httpx/config_tts.go. State-changing routes ride the
// double-submit CSRF token via withCSRF() per Phase-1 D-14.
import {
  type TTSConfig,
  type TTSGoogleSettings,
  type TurnTimeouts,
  type ChildProfile,
} from "../types/api.js";
import { withCSRF } from "./_csrf.js";

// TTSConfigError carries the structured error_code from the backend so
// the FE can branch on a known machine-readable identifier (e.g.
// "voice_not_rendered") rather than parsing English message strings.
export class TTSConfigError extends Error {
  constructor(
    public code: string,
    public httpStatus: number,
    message: string,
  ) {
    super(message);
    this.name = "TTSConfigError";
  }
}

// parseStructuredError reads {error_code, message} from a 4xx response.
// On failure (non-JSON body), returns a TTSConfigError with code "unknown".
async function parseStructuredError(label: string, res: Response): Promise<TTSConfigError> {
  try {
    const body = await res.json() as { error_code?: string; message?: string };
    return new TTSConfigError(
      body.error_code ?? "unknown",
      res.status,
      body.message ?? `${label}: ${res.status}`,
    );
  } catch {
    return new TTSConfigError("unknown", res.status, `${label}: ${res.status}`);
  }
}

export async function getTTSConfig(): Promise<TTSConfig> {
  const res = await fetch("/api/config/tts", { credentials: "include" });
  if (!res.ok) throw new Error(`getTTSConfig: ${res.status}`);
  return res.json() as Promise<TTSConfig>;
}

export async function setTTSChoice(
  provider: string,
  voice: string,
): Promise<TTSConfig> {
  const res = await fetch("/api/config/tts", {
    method: "PUT",
    credentials: "include",
    headers: withCSRF({ "Content-Type": "application/json" }),
    body: JSON.stringify({ provider, voice }),
  });
  if (!res.ok) {
    // 422 voice_not_rendered carries a structured body the FE can switch on.
    if (res.status === 422) throw await parseStructuredError("setTTSChoice", res);
    throw new Error(`setTTSChoice: ${res.status}`);
  }
  return res.json() as Promise<TTSConfig>;
}

export async function getTurnTimeouts(): Promise<TurnTimeouts> {
  const res = await fetch("/api/config/timeouts", { credentials: "include" });
  if (!res.ok) throw new Error(`getTurnTimeouts: ${res.status}`);
  return res.json() as Promise<TurnTimeouts>;
}

export async function setTurnTimeouts(
  settings: TurnTimeouts,
): Promise<TurnTimeouts> {
  const res = await fetch("/api/config/timeouts", {
    method: "PUT",
    credentials: "include",
    headers: withCSRF({ "Content-Type": "application/json" }),
    body: JSON.stringify(settings),
  });
  if (!res.ok) throw new Error(`setTurnTimeouts: ${res.status}`);
  return res.json() as Promise<TurnTimeouts>;
}

// Player profile (name / age / sex) on the singleton app_user row.
// Mirrors internal/httpx/config_profile.go. The turn handler reads this
// per-turn to expand {{PlayerName}} / {{PlayerAge}} / {{PlayerSex}} in a
// game's system prompt.
export async function getProfile(): Promise<ChildProfile> {
  const res = await fetch("/api/config/profile", { credentials: "include" });
  if (!res.ok) throw new Error(`getProfile: ${res.status}`);
  return res.json() as Promise<ChildProfile>;
}

export async function setProfile(profile: ChildProfile): Promise<ChildProfile> {
  const res = await fetch("/api/config/profile", {
    method: "PUT",
    credentials: "include",
    headers: withCSRF({ "Content-Type": "application/json" }),
    body: JSON.stringify(profile),
  });
  if (!res.ok) throw new Error(`setProfile: ${res.status}`);
  return res.json() as Promise<ChildProfile>;
}

// Mic-open-beep default — the operator-controlled fallback used by the
// /config screen when the device has no per-user override.
export interface MicBeepDefault {
  mic_open_beep_default: boolean;
}

export async function getMicBeepDefault(): Promise<MicBeepDefault> {
  const res = await fetch("/api/config/mic-beep", { credentials: "include" });
  if (!res.ok) throw new Error(`getMicBeepDefault: ${res.status}`);
  return res.json() as Promise<MicBeepDefault>;
}

export async function setTTSGoogle(
  settings: TTSGoogleSettings,
): Promise<TTSGoogleSettings> {
  const res = await fetch("/api/config/tts/google", {
    method: "PUT",
    credentials: "include",
    headers: withCSRF({ "Content-Type": "application/json" }),
    body: JSON.stringify(settings),
  });
  if (!res.ok) throw new Error(`setTTSGoogle: ${res.status}`);
  return res.json() as Promise<TTSGoogleSettings>;
}
